/*
This service contains all import field objects and import helper functions for Content Tools place/modifiers import and PCMWebUI Stops import.
*/
/* globals ALKMaps, _ */
const config = require("config");

(function () {
  "use strict";

  angular
    .module("alk.import")
    .factory("alkImportService", alkImportService);

  alkImportService.$inject = ["$q", "PCMRegion", "UserProvider", "$http", "alkModalService", "$translate", "placeCloudService", "MAXDBLENGTHS", "STATES",
    "COUNTRIES", "$injector"];

  function alkImportService($q, PCMRegion, UserProvider, $http, alkModalService, $translate, placeCloudService, MAXDBLENGTHS, STATES, COUNTRIES, $injector) {

    const INVALID_CHAR_REGEX = /^[^\^/\\]+$/;

    //The base import field ES6 class. It holds metadata for an import field (i.e. place name, latitude).
    //All import fields will extend from this class.
    //NOTE: does not store the actual value read from CSV. This is done in importFieldsModal.controller.js.
    class BaseImportField {

      constructor(params) {
        this.name = params.name;  //Internal name of the import field, used by the code only.
        this.translationId = params.translationId;  //Translation ID of the import field that contains the translated user-facing name of the field.
        this.maxLength = params.maxLength;  //The max amount of characters this field can have before the import system throws an error.
        //List of necessary import fields: throws error if this field exists, but a dependency does not.
        this.dependencies = [];
        if (params.dependencies)
          //Returns an array of ImportField objects; since they are ES6 classes, they must be instantiated first
          this.dependencies = params.dependencies.map(Dependency => new (Dependency)());
      }

      //Check if the value provided as header is a suitable header for this import field type (i.e. Place Name, Latitude, etc.)
      suitableHeader(headerVal) {
        return headerVal.toLowerCase().includes(this.name.toLowerCase()) || headerVal.toLowerCase().includes(this.getTranslatedName());
      }

      //Get a translated name to use in messages (i.e. place name)
      getTranslatedName() {
        return $translate.instant(this.translationId).toLowerCase();
      }

      //Get a translated name to use in messages in title case. (i.e. Place Name)
      getTranslatedNameTitleCase() {
        return $translate.instant(this.translationId);
      }

      //Returns an array of strings. To be overridden by subclasses as necessary.
      getErrors(fieldValue, fieldParams) {
        return [];
      }
    }

    /////////////////////////////////////////////////////////////////////////
    //COMMON FIELDS (SHARED BY BOTH CONTENTTOOLS PLACES AND PCMWEBUI STOPS)//
    /////////////////////////////////////////////////////////////////////////

    class LatitudeField extends BaseImportField {
      constructor() {
        super({
          name: "Latitude",
          translationId: "common.Latitude"
        });
      }
      suitableHeader(headerVal) {
        return headerVal.toLowerCase() === "lat" || super.suitableHeader(headerVal);
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) return [];
        if (!isLatValid(fieldValue)) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) + " (" + $translate.instant("common.import.invalidLatitude") + ")" ];
        }
        return [];
      }
    }

    class LongitudeField extends BaseImportField {
      constructor() {
        super({
          name: "Longitude",
          translationId: "common.Longitude"
        });
      }
      suitableHeader(headerVal) {
        return headerVal.toLowerCase() === "lon" || super.suitableHeader(headerVal);
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) return [];
        if (!isLonValid(fieldValue)) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) + " (" + $translate.instant("common.import.invalidLongitude") + ")" ];
        }
        return [];
      }
    }

    class LatLonField extends BaseImportField {
      constructor() {
        super({
          name: "LatLon",
          translationId: "common.latLon"
        });
      }
      suitableHeader(headerVal) {
        const latIndex = headerVal.toLowerCase().indexOf("lat");
        const lonIndex = headerVal.toLowerCase().indexOf("lon");
        return (latIndex !== -1 && lonIndex !== -1 && latIndex < lonIndex) || super.suitableHeader(headerVal);
      }
      getErrors(fieldValue, fieldParams) {
        const fieldErr = $translate.instant("common.import.fieldErrInvalid", fieldParams);
        if (!fieldValue)
          return [];
        const [lat, lon] = fieldValue.split(",");
        if (_.isUndefined(lon))
          return [ `${fieldErr} (${$translate.instant("common.import.invalidLatLonNoComma")})` ];

        if (!isLatValid(lat) || !isLonValid(lon))
          return [ `${fieldErr} (${$translate.instant("common.import.invalidLatLon")})` ];

        return [];
      }
    }

    class StreetAddressField extends BaseImportField {
      constructor() {
        super({
          name: "StreetAddress",
          translationId: "common.StreetAddress",
          maxLength: MAXDBLENGTHS.STREET
        });
      }
      suitableHeader(headerVal) {
        return headerVal.includes("street") || headerVal.includes("address");
      }
    }

    class CityField extends BaseImportField {
      constructor() {
        super({
          name: "City",
          translationId: "common.City",
          maxLength: MAXDBLENGTHS.CITY
        });
      }
      getErrors(fieldValue, fieldParams) {
        return validateNoInvalidChar(fieldValue, fieldParams);
      }
    }

    class StateField extends BaseImportField {
      constructor() {
        super({
          name: "State",
          translationId: "common.State"
        });
        this.statesList = (UserProvider.getUser().region === "NA") ? STATES : [];
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) {
          return [];
        }
        if (this.statesList.length < 1) {
          return [];
        }
        const found = this.statesList.some(state => {
          return state.abbreviation.toLowerCase() === fieldValue.toLowerCase();
        });
        if (!found) {
          return [
            `${$translate.instant("common.import.fieldErrInvalid", fieldParams)} \
              (${$translate.instant("common.import.invalidState")})`
          ];
        }
        return [];
      }
      setRegion() {
        this.statesList = (UserProvider.getUser().region === "NA") ? STATES : [];
      }
    }

    class ZipField extends BaseImportField {
      constructor() {
        super({
          name: "Zip",
          translationId: "common.Zip",
          maxLength: MAXDBLENGTHS.ZIP
        });
      }
    }

    class CountyField extends BaseImportField {
      constructor() {
        super({
          name: "County",
          translationId: "common.County",
          maxLength: MAXDBLENGTHS.COUNTRY
        });
      }
    }

    class CountryField extends BaseImportField {
      constructor() {
        super({
          name: "Country",
          translationId: "common.Country",
          maxLength: MAXDBLENGTHS.COUNTRY
        });
      }
    }

    ///////////////////////////////////////
    //CONTENTTOOLS SPECIFIC PLACES FIELDS//
    ///////////////////////////////////////

    class PlaceNameField extends BaseImportField {
      constructor() {
        super({
          name: "PlaceName",
          translationId: "templates.importPlaces.placeNameField",
          maxLength: MAXDBLENGTHS.PLACE_NAME
        });
      }
      suitableHeader(headerVal) {
        return _.includes(headerVal, "name");
      }
      getErrors(fieldValue, fieldParams) {
        return validateNoInvalidChar(fieldValue, fieldParams);
      }
    }

    class CompanyIdField extends BaseImportField {
      constructor() {
        super({
          name: "CompanyId",
          translationId: "common.PID",
          maxLength: MAXDBLENGTHS.CID
        });
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) {
          return [];
        }
        if (!(/^[A-Za-z0-9]*$/).test(fieldValue)) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) + 
            " (" + $translate.instant("common.import.invalidCompanyId") + "): " + fieldValue];
        }
        return [];
      }
    }

    class CountryAbbrevField extends BaseImportField {
      // pchen: back-compat with State (e.g., GB) export
      constructor() {
        super({
          name: "CountryAbbreviation",
          translationId: "common.CountryAbbreviation"
        });
        this.countryList = COUNTRIES[UserProvider.getUser().region];
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) {
          return [];
        }
        const countries = this.countryList;
        const found = countries.some(country => {
          return country.abbreviation.toLowerCase() === fieldValue.toLowerCase();
        });
        if (!found) {
          return [
            `${$translate.instant("common.import.fieldErrInvalid", fieldParams)} \
              (${$translate.instant("common.import.invalidState")})`
          ];
        }
        return [];
      }
      setRegion() {
        this.countryList = COUNTRIES[UserProvider.getUser().region];
      }
    }

    class PhoneField extends BaseImportField {
      constructor() {
        super({
          name: "Phone",
          translationId: "common.PhoneNumber",
          maxLength: MAXDBLENGTHS.PHONE
        });
      }
      getErrors(fieldValue, fieldParams) {
        return validateNoInvalidChar(fieldValue, fieldParams);
      }
    }

    class CommentsField extends BaseImportField {
      constructor() {
        super({
          name: "Comments",
          translationId: "templates.importPlaces.commentField",
          maxLength: MAXDBLENGTHS.PLACE_COMMENTS
        });
      }
    }

    class YardNameField extends BaseImportField {
      constructor() {
        super({
          name: "YardName",
          translationId: "templates.importPlaces.yardNameField",
          maxLength: MAXDBLENGTHS.YARD_NAME,
        });
      }
      getErrors(fieldValue, fieldParams) {
        return validateNoInvalidChar(fieldValue, fieldParams);
      }
    }

    class YardPointsField extends BaseImportField {
      constructor() {
        super({
          name: "YardPoints",
          translationId: "templates.importPlaces.yardPointsField",
        });
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) {
          return []; //Can be blank
        }
        const fieldErr = $translate.instant("common.import.fieldErrInvalid", fieldParams);
        const errs = [ fieldErr ];
        const pieces = fieldValue.split("|");
        if (pieces < 3) {
          return errs; //Must have 3 points at least
        }
        // check all pieces valid
        if (!allLatLonValid(pieces)) {
          return errs;
        }

        const mapUtilities = $injector.get("mapUtilities");
        const coords = getYardPointsFromString(fieldValue).map(coord => {
          const mercLL = mapUtilities.LLtoMerc(coord[0], coord[1]);
          return new ALKMaps.Geometry.Point(mercLL.lon, mercLL.lat);
        });
        if (mapUtilities.isPolygonSelfIntersecting(new ALKMaps.Geometry.LinearRing(coords))) {
          const selfIntersectErr = $translate.instant("templates.importPlaces.siteSelfIntersecting");
          return [ `${fieldErr}: ${selfIntersectErr}` ];
        }

        return [];
      }
    }

    class GatesField extends BaseImportField {
      constructor() {
        super({
          name: "Gates",
          translationId: "templates.importPlaces.gates",
          dependencies: [YardPointsField]
        });
      }
      getErrors(fieldValue, fieldParams) {
        const enums = {
          GATE_TYPE: {
            ENTRY: "0",
            EXIT: "1",
            ENTRY_EXIT: "2"
          },
          RESTRICT: {
            FALSE: "0",
            TRUE: "1"
          }
        };

        if (!fieldValue) {
          return [];
        }
        const errors = [];

        const escPipeChar = String.fromCharCode(13);
        const escCaretChar = String.fromCharCode(14);
        const escBackslashChar = String.fromCharCode(15);

        fieldValue = getDecodedImportString(fieldValue, escPipeChar, escCaretChar, escBackslashChar);

        const gates = [];
        const gateStrs = fieldValue.split("^");

        if (gateStrs.length > 5) {
          errors.push(createGateError(fieldParams, "templates.importPlaces.tooManyGates"));
        }

        for (let i = 0; i < gateStrs.length; i++) {
          const gateStr = gateStrs[i];
          const pieces = gateStr.split("|");
          const gateNum = i + 1;

          if (pieces.length !== 6) {
            errors.push(createGateError(fieldParams, "templates.importPlaces.badGateFormat", { gateNum }));
          } else {
            const [latlon, latlonSnap, gateType, autoRestrict, truckRestrict, note] = pieces;

            if (getLatLonFromString(latlon) === null) {
              errors.push(createGateError(fieldParams, "templates.importPlaces.gateLatLonInvalid", { gateNum }));
            }
            if (getLatLonFromString(latlonSnap) === null) {
              errors.push(createGateError(fieldParams, "templates.importPlaces.gateLatLonSnapInvalid", { gateNum }));
            }
            if (!_.includes(enums.GATE_TYPE, gateType)) {
              errors.push(createGateError(fieldParams, "templates.importPlaces.gateTypeInvalid", { gateNum }));
            }
            if (!_.includes(enums.RESTRICT, autoRestrict)) {
              errors.push(createGateError(fieldParams, "templates.importPlaces.gateAutoRestrictInvalid", { gateNum }));
            }
            if (!_.includes(enums.RESTRICT, truckRestrict)) {
              errors.push(createGateError(fieldParams, "templates.importPlaces.gateTruckRestrictInvalid", { gateNum }));
            }
            if (autoRestrict === enums.RESTRICT.TRUE && truckRestrict === enums.RESTRICT.TRUE) {
              errors.push(createGateError(fieldParams, "templates.importPlaces.gateCannotRestrictBoth", { gateNum }));
            }
            if (note.length > MAXDBLENGTHS.YARD_GATE_NOTE) {
              errors.push(createGateError(fieldParams, "templates.importPlaces.fieldErrGateNoteTooLong", { maxLength: MAXDBLENGTHS.YARD_GATE_NOTE, gateNum }));
            }
          }
        }
        return errors;
      }
    }

    class EntryLatLonField extends BaseImportField {
      constructor() {
        super({
          name: "EntryLatLon",
          translationId: "templates.importPlaces.entryLatLonField",
          dependencies: [YardPointsField]
        });
      }
      getErrors(fieldValue, fieldParams) {
        if(!isLatLonStringValid(fieldValue)) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) ];
        }
        return [];
      }
    }

    class EntryLatLonSnapField extends BaseImportField {
      constructor() {
        super({
          name: "EntryLatLonSnap",
          translationId: "templates.importPlaces.entryLatLonSnapField",
          dependencies: [YardPointsField]
        });
      }
      getErrors(fieldValue, fieldParams) {
        if(!isLatLonStringValid(fieldValue)) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) ];
        }
        return [];
      }
    }

    class EntryNotesField extends BaseImportField {
      constructor() {
        super({
          name: "EntryNotes",
          translationId: "templates.importPlaces.entryNotesField",
          maxLength: MAXDBLENGTHS.YARD_GATE_NOTE,
          dependencies: [YardPointsField]
        });
      }
    }

    class ExitLatLonField extends BaseImportField {
      constructor() {
        super({
          name: "ExitLatLon",
          translationId: "templates.importPlaces.exitLatLonField",
          dependencies: [YardPointsField]
        });
      }
      getErrors(fieldValue, fieldParams) {
        if(!isLatLonStringValid(fieldValue)) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) ];
        }
        return [];
      }
    }

    class ExitLatLonSnapField extends BaseImportField {
      constructor() {
        super({
          name: "ExitLatLonSnap",
          translationId: "templates.importPlaces.exitLatLonSnapField",
          dependencies: [YardPointsField]
        });
      }
      getErrors(fieldValue, fieldParams) {
        if(!isLatLonStringValid(fieldValue)) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) ];
        }
        return [];
      }
    }

    class ExitNotesField extends BaseImportField {
      constructor() {
        super({
          name: "ExitNotes",
          translationId: "templates.importPlaces.exitNotesField",
          maxLength: MAXDBLENGTHS.YARD_GATE_NOTE,
          dependencies: [YardPointsField]
        });
      }
    }

    class SiteDetailField extends BaseImportField {
      constructor() {
        super({
          name: "SiteDetail",
          translationId: "templates.importPlaces.siteDetailField",
          maxLength: MAXDBLENGTHS.SITE_DETAIL
        });
      }
      getErrors(fieldValue, fieldParams) {
        return validateNoInvalidChar(fieldValue, fieldParams);
      }
    }

    ///////////////////////////////////////////
    //PCM WEB UI STOPS SPECIFIC IMPORT FIELDS//
    ///////////////////////////////////////////

    class StopNameField extends BaseImportField {
      constructor() {
        super({
          name: "StopName",
          translationId: "templates.importStops.stopNameField",
          maxLength: 100,
        });
      }
      suitableHeader(headerVal) {
        return super.suitableHeader(headerVal) || headerVal.includes("name");
      }
    }

    class EstimatedDurationField extends BaseImportField {
      constructor() {
        super({
          name: "EstimatedDuration",
          translationId: "templates.importStops.estimatedDurationField",
          maxLength: 100
        });
      }
      suitableHeader(headerVal) {
        return super.suitableHeader(headerVal) || headerVal.includes("duration");
      }
      getErrors(fieldValue, fieldParams) {
        var stopService = $injector.get("stopService");
        if (!fieldValue) {
          return [];
        }
        if(stopService.getHoursFromString(fieldValue) === null) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) + " (" + $translate.instant("templates.importStops.invalidDuration") + ")" ];
        }
        return [];
      }
    }

    class EarliestArrivalTimeField extends BaseImportField {
      constructor() {
        super({
          name: "EarliestArrivalTime",
          translationId: "templates.importStops.earliestArrivalTimeField",
          maxLength: 100
        });
      }
      suitableHeader(headerVal) {
        return super.suitableHeader(headerVal) || headerVal.includes("earliest");
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) {
          return [];
        }
        if(!moment(fieldValue, "HH:mm").isValid()) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) + " (" + $translate.instant("templates.importStops.invalidEarliestArrivalTime") + ")" ];
        }
        return [];
      }
    }

    class LatestArrivalTimeField extends BaseImportField {
      constructor() {
        super({
          name: "LatestArrivalTime",
          translationId: "templates.importStops.latestArrivalTimeField",
          maxLength: 100
        });
      }
      suitableHeader(headerVal) {
        return super.suitableHeader(headerVal) || headerVal.includes("latest");
      }
      getErrors(fieldValue, fieldParams) {
        if (!fieldValue) {
          return [];
        }
        if(!moment(fieldValue, "HH:mm").isValid()) {
          return [ $translate.instant("common.import.fieldErrInvalid", fieldParams) + " (" + $translate.instant("templates.importStops.invalidLatestArrivalTime") + ")" ];
        }
        return [];
      }
    }

    ////////////////////
    //HELPER FUNCTIONS//
    ////////////////////

    //If FileReader is undefined, the import CSV feature won't work
    function browserImportIncompatible() {
      var oldBrowser = false;
      try {
        var fileReaderTest = FileReader;
      } catch (err) {
        oldBrowser = true;
      }
      return oldBrowser;
    }

    function createGateError(fieldParams, translateKey, gateParams) {
      const fieldErr = $translate.instant("common.import.fieldErrInvalid", fieldParams);
      const gateErr = $translate.instant(translateKey, gateParams);
      return `${fieldErr} (${gateErr})`;
    }

    function filterErrors(errs) {
      //Hide rest of errors if over 100 AND 50 more will be hidden
      if(errs.length > 100) {
        var hiddenErrCount = errs.length - 100;
        if(hiddenErrCount >= 50) {
          errs = errs.slice(0, 100);
          errs.push($translate.instant("common.import.tooManyErrors", { num: hiddenErrCount }));
        }
      }
      return errs;
    }

    function testCompanyIds(cidList) {
      return placeCloudService.testCompanyIds(cidList);
    }

    function importPlaces(places, importType) {
      var wizardData = alkModalService.getCurrentWizardData();
      var setName = wizardData.setName;
      var setId = wizardData.selectedSet ? wizardData.selectedSet.Id : "";
      var category = wizardData.selectedCategory; // should only be used if importing New Set
      return placeCloudService.getCategoryForSet(wizardData.selectedSet).then(catId => {
        if (wizardData.selectedSet && wizardData.selectedSet.Categories &&  wizardData.selectedSet.Categories[0]) {
          catId = wizardData.selectedSet.Categories[0].Id;
        }
        var setOptions = wizardData.copilotOptions;

        places = _.map(places, row => {
          var place = {};
          place.placeName = row.PlaceName;
          place.companyId = row.CompanyId;
          place.latitude = row.Latitude;
          place.longitude = row.Longitude;
          place.street = row.StreetAddress;
          place.city = row.City;
          place.state = row.State;
          place.zip = row.Zip;
          place.country = row.Country;
          place.phone = row.Phone;
          place.info = row.Comments;
          place.yardName = row.YardName;
          place.yardPoints = getYardPointsFromString(row.YardPoints);
          place.gates = getGatesFromString(row.Gates);

          var entryLatLon = getLatLonFromString(row.EntryLatLon);
          var entryLatLonSnap = getLatLonFromString(row.EntryLatLonSnap);
          var entryNotes = row.EntryNotes;
          if (entryLatLon && entryLatLonSnap) {
            var entryGate = {
              Coords: entryLatLon,
              SnapCoords: entryLatLonSnap,
              Type: 0,
              AutoRestricted: false,
              TruckRestricted: false,
              NoteContent: entryNotes
            };
            place.gates.push(entryGate);
          }

          var exitLatLon = getLatLonFromString(row.ExitLatLon);
          var exitLatLonSnap = getLatLonFromString(row.ExitLatLonSnap);
          var exitNotes = row.ExitNotes;
          if (exitLatLon && exitLatLonSnap) {
            var exitGate = {
              Coords: exitLatLon,
              SnapCoords: exitLatLonSnap,
              Type: 1,
              AutoRestricted: false,
              TruckRestricted: false,
              NoteContent: exitNotes
            };
            place.gates.push(exitGate);
          }

          // don't need to send "" to database
          if (row.SiteDetail) {
            place.siteDetail = row.SiteDetail;
          }

          return place;
        });

        return setPLIDsForPlaces(places).then(() => {
          var deferred = $q.defer();
          var promise;
          if (importType === 0) {
            promise = importPlacesNewSet(setName, places, setOptions, category);
          } else if (importType === 1) {
            promise = importPlacesAppend(setId, catId, places);
          } else if (importType === 2) {
            promise = importPlacesReplace(setId, catId, places);
          }
          promise.then(result => {
            if (!result) {
              deferred.reject($translate.instant("common.import.unknownError"));
            } else {
              deferred.resolve(result);
            }
          });
          return deferred.promise;
        });
      });
    }

    //Return array of plid promises for each this place's gates
    function setClosestPLIDToLonLat(place) {
      let linkWebService = $injector.get("linkWebService");
      let promises = [];
      for(let gate of place.gates) {
        let lon = gate.SnapCoords.Lon;
        let lat = gate.SnapCoords.Lat;
        let deferred = $q.defer();
        let left = lon;
        let bottom = lat;
        let right = lon;
        let top = lat;
        let bounds = new ALKMaps.Bounds(Number(left) - 0.001, Number(bottom) + 0.001, Number(right) + 0.001, Number(top) - 0.001);
        let mapDataVersion = UserProvider.getUser().dataVersion || "Current";
        let promise = linkWebService.getLinks(bounds.toBBOX(), mapDataVersion).then(function (results) {
          //console.log(results.data.features);
          let plid = "";
          let min = Number.POSITIVE_INFINITY;
          let minResult = null;
          for(let result of results.data.features) {
            let points = _.map(result.geometry.coordinates, function(coord) {
              return new ALKMaps.Geometry.Point(coord[0], coord[1]);
            });
            let linkGeom = new ALKMaps.Geometry.LineString(points);
            let dist = linkGeom.distanceTo(new ALKMaps.Geometry.Point(lon, lat));
            if(dist < min) {
              min = dist;
              minResult = result;
            }
          }
          if(minResult && minResult.properties.plid) {
            plid = minResult.properties.plid;
          }
          gate.plid = plid;
        });
        promises.push(promise);
      }
      return promises;
    }

    //Need to get PLID from snap coords before importing
    function setPLIDsForPlaces(places) {
      var deferred = $q.defer();
      var plidPromises = [];

      for(let place of places) {
        plidPromises = plidPromises.concat(setClosestPLIDToLonLat(place));
      }

      $q.all(plidPromises).then(function() {
        deferred.resolve();
      });
      return deferred.promise;
    }

    //Helper function used by the import functions below that create the places and yards
    function importPlacesAndYardsHelper(setId, catId, places) {
      var deferred = $q.defer();
      var placesWithoutYards = _.remove(places, function (place) {
        return !place.yardPoints; //No points, no yard. Must have points for any of the other yard fields to make sense
      });
      var placeIds, yardIds;

      _.each(places, function (place, index) {
        //If the place doesn't have a yard name, give it one
        if (!place.yardName) {
          place.yardName = $translate.instant("templates.yards.unnamedYard");
        }
      });

      //Add the places without yards (this is batched for performance)
      placeCloudService.saveNewPlaces(setId, catId, placesWithoutYards).then(function (response) {
        if (!response) {
          deferred.resolve(null);
          return;
        }
        //If there are no places with yards, just quit here
        if (places.length === 0) {
          deferred.resolve({
            setId: setId
          });
          return;
        }
        //Add the places with yards
        addPlaceYardRecursiveHelper(0, catId);
      });

      return deferred.promise;

      //Initiates the callback chain for adding a single place and its yard data.
      function addPlaceYardRecursiveHelper(index, catId) {
        if (index >= places.length) {
          deferred.resolve({
            setId: setId
          });
          return;
        }
        var place = places[index];
        var yardId;
        //Add place
        placeCloudService.saveNewPlace(setId, catId, place).then(response => {
          if (!response) {
            deferred.resolve(null);
            return;
          }
          place.Id = response.Id;
          //Add yard
          return placeCloudService.addYard({
            yardName: place.yardName,
            shapePoints: place.yardPoints,
            siteDetail: place.siteDetail
          });
        }).then(response => {
          if (!response) {
            deferred.resolve(null);
            return;
          }
          yardId = response.Id;
          //Add place to yard
          return placeCloudService.addPlaceToYard(yardId, place.Id, true);
        }).then(response => {
          if (!response) {
            deferred.resolve(null);
            return;
          }
          //Add all yard gates
          return placeCloudService.addYardGates(yardId, place.gates)
            .then(gatesRes => {
              //Any one failed: return failed
              if (gatesRes.indexOf(null) > -1) {
                deferred.resolve(null);
                return;
              }
              return response;
            });
        }).then(response => {
          if (!response) {
            deferred.resolve(null);
            return;
          }
          addPlaceYardRecursiveHelper(index + 1, catId);
        });
      }
    }

    function importPlacesNewSet(setName, places, options, category) {
      var deferred = $q.defer();
      var setId = "";
      let setStyle = category;
      //Add the place set
      placeCloudService.addPlaceSet(setName, null, true, options, setStyle).then(function (response) {
        if (!response) {
          deferred.resolve(null);
          return;
        }
        setId = response.Id;
        placeCloudService.addCategoryForSet(setName, setId, options, category.Icon).then(function (catResponse) {
          importPlacesAndYardsHelper(setId, catResponse.Id, places).then(function(placesResponse){
            if (!placesResponse)
              // pchen: if failed, manually rollback places creation
              placeCloudService.deletePlaceSet(setId);
            deferred.resolve(placesResponse);
          });
        });
      });

      return deferred.promise;
    }

    function importPlacesAppend(setId, catId, places) {
      var deferred = $q.defer();
      //Add the places
      importPlacesAndYardsHelper(setId, catId, places).then(function (response) {
        deferred.resolve(response);
      });
      return deferred.promise;
    }

    function importPlacesReplace(setId, catId, places) {
      var deferred = $q.defer();
      //Get the places in the set
      placeCloudService.getPlaces(setId).then(function (getResponse) {
        if (!getResponse) {
          deferred.resolve(null);
          return;
        }
        var placeIdList = [];
        _.each(getResponse.Places, function (place) {
          placeIdList.push(place.Id);
        });
        return placeCloudService.deletePlaces(placeIdList);
        //Delete the places in the set
      }).then(function (deleteResponse) {
        if (!deleteResponse) {
          deferred.resolve(null);
          return;
        }
        importPlacesAndYardsHelper(setId, catId, places).then(function (importResponse) {
          deferred.resolve(importResponse);
        });
      });
      return deferred.promise;
    }

    //"file" is js file input variable (i.e. files[0])
    function importExceptions(file, setId, setName, replace) {
      var deferred = $q.defer();
      /*deferred.resolve();
      return deferred.promise;
      */
      var reader = new FileReader();
      reader.onload = function () {
        var base64EncodedString = reader.result;
        base64EncodedString = base64EncodedString.split(",")[1];
        var b = atob(base64EncodedString);
        var byteNumbers = new Array(b.length);
        for (var i = 0; i < b.length; i++) {
          byteNumbers[i] = b.charCodeAt(i);
        }
        var request = {
          method: "POST",
          url: config.pcmwsUrl + "AvoidFavorSets/import",
          headers: {
            Authorization: config.alkmapsApiKey
          },
          params: {
            dataset: UserProvider.getUser().dataset || UserProvider.getUser().dataVersion || "Current"
          },
          data: {
            SetName: !setId ? setName : "",
            SetTag: null,
            SetID: !setId ? -1 : setId,
            FileBytes: byteNumbers,
            Replace: !replace ? false : replace
          }
        };
        $http(request)
          .then(function (response) { //success
            deferred.resolve(response.data);
          }, function (response) { //fail
            deferred.reject(response);
          });

      };
      reader.onerror = function (err) {
        deferred.reject(err);
      };
      reader.readAsDataURL(file);
      return deferred.promise;

    }

    function isLatLonStringValid(latLonString) {
      if (!latLonString) {
        return true; //Can be blank
      }
      var coords = getLatLonFromString(latLonString);
      if (!coords) {
        return false;
      }
      // fail on null lat/lon
      if (coords.Lat === 0 && coords.Lon === 0) {
        return false;
      }
      return true;
    }

    function getLatLonFromString(str) {
      if (!str) {
        return null;
      }
      var pieces = str.split(",");
      if (pieces.length < 2) {
        return null;
      }
      if (!isLatValid(pieces[0]) || !isLonValid(pieces[1])) {
        return null;
      }
      return {
        Lat: Number(pieces[0]),
        Lon: Number(pieces[1])
      };
    }

    function allLatLonValid(coordStrs) {
      return coordStrs.every(coordStr => {
        const latLonSplit = coordStr.split(",");
        //Each coord must have a lat and lon
        if (latLonSplit.length < 2) {
          return false;
        }
        const lat = Number(latLonSplit[0]);
        const lon = Number(latLonSplit[1]);
        //They must be valid lat/lons
        if (!isLatValid(lat) || !isLonValid(lon)) {
          return false;
        }
        return true;
      });
    }

    function isLatLonValid(fieldValue, maxBound) {
      if (isNaN(fieldValue) || Number(fieldValue) < -maxBound || Number(fieldValue) > maxBound) {
        return false;
      }
      return true;
    }

    function isLatValid(fieldValue) {
      return isLatLonValid(fieldValue, 90);
    }

    function isLonValid(fieldValue) {
      return isLatLonValid(fieldValue, 180);
    }

    function getYardPointsFromString(importStr) {
      if (!importStr) {
        return null;
      }
      var points = [];
      _.each(importStr.split("|"), function (coordStr) {
        var lat = Number(coordStr.split(",")[0]);
        var lon = Number(coordStr.split(",")[1]);
        points.push([lon, lat]);
      });
      return points;
    }

    function getGatesFromString(importStr) {
      if(!importStr) {
        return [];
      }
      var gatesField = new GatesField();
      if(gatesField.getErrors(importStr, {}).length > 0) {
        return [];
      }

      var escPipeChar = String.fromCharCode(13);
      var escCaretChar = String.fromCharCode(14);
      var escBackslashChar = String.fromCharCode(15);

      //Gates is double delimited by ^ for gates and | for the gate fields.
      //So the import string must escape '^', '|' and '\' chars by prefacing with a '\' char.
      importStr = getDecodedImportString(importStr, escPipeChar, escCaretChar, escBackslashChar);

      var gates = [];
      var gateStrs = importStr.split("^");
      for(var gateStr of gateStrs) {
        var pieces = gateStr.split("|");
        var gate = {
          Coords: {
            Lat: pieces[0].split(",")[0],
            Lon: pieces[0].split(",")[1],
          },
          SnapCoords: {
            Lat: pieces[1].split(",")[0],
            Lon: pieces[1].split(",")[1],
          },
          Type: Number(pieces[2]),
          AutoRestricted: (pieces[3] === "1") ? true : false,
          TruckRestricted: (pieces[4] === "1") ? true : false,
          NoteContent: pieces[5].replace(new RegExp(escPipeChar, "g"), "|")
            .replace(new RegExp(escCaretChar, "g"), "^")
            .replace(new RegExp(escBackslashChar, "g"), "\\")
        };
        gates.push(gate);
      }
      return gates;
    }

    //Encode the note by escaping '^', '|' and '\' chars by prefacing with a '\' char.
    function getEncodedNote(noteString) {
      noteString = noteString.replace(/\\/g, "\\\\");
      noteString = noteString.replace(/\|/g, "\\\|");
      noteString = noteString.replace(/\^/g, "\\\^");
      return noteString;
    }

    //Reads importStr from left to right.
    //Upon encountering \\, replaces with escBackslashChar.
    //Upon encountering \|, replaces with escPipeChar.
    //Upon encountering \^, replaces with escCaretChar.
    function getDecodedImportString(importStr, escPipeChar, escCaretChar, escBackslashChar) {
      var c16 = String.fromCharCode(16);

      var importStrArray = importStr.split("");
      for(var i = 0; i < importStr.length - 1; i++) {
        var charPair = importStr.substring(i, i + 2);
        if(charPair === "\\\\") {
          importStrArray[i] = escBackslashChar;
          importStrArray[i+1] = c16;
          i++;
        } else if(charPair === "\\|") {
          importStrArray[i] = escPipeChar;
          importStrArray[i+1] = c16;
          i++;
        } else if(charPair === "\\^") {
          importStrArray[i] = escCaretChar;
          importStrArray[i+1] = c16;
          i++;
        }
      }
      importStr = importStrArray.join("");
      importStr = importStr.replace(new RegExp(c16, "g"), "");
      return importStr;
    }

    function strContainsLonLat(importStr, lon, lat, threshold) {
      if (!importStr) {
        return true;
      }
      var coords = getYardPointsFromString(importStr);
      return containsLonLat(coords, lon, lat, threshold);
    }

    function validateNoInvalidChar(fieldValue, fieldParams) {
      if (!fieldValue) {
        return [];
      }
      if (!INVALID_CHAR_REGEX.test(fieldValue)) {
        return [$translate.instant("common.import.fieldErrInvalid", fieldParams) +
          " (" + $translate.instant("common.import.canNotContainSpecialCharacters") + "): " + fieldValue
        ];
      }
      return [];
    }

    //Note: will return true for invalid (i.e. blank) paramters. Reason is because "place not in site" error should not show if lat/lon/importCoords are invalid
    function containsLonLat(importCoords, lon, lat, threshold) {
      var mapUtilities = $injector.get("mapUtilities");
      if(!importCoords || !lon || !lat) {
        return true;
      }
      let poly = [];
      var mercLL;
      for(var coords of importCoords) {
        mercLL = mapUtilities.LLtoMerc(coords[0], coords[1]);
        poly.push(new ALKMaps.Geometry.Point(mercLL.lon, mercLL.lat));
      }
      let ring = new ALKMaps.Geometry.LinearRing(poly);
      mercLL = mapUtilities.LLtoMerc(lon, lat);
      var point = new ALKMaps.Geometry.Point(mercLL.lon, mercLL.lat);
      if (ring.containsPoint(point) && ring.distanceTo(point) !== 0) {
        return true;
      } else if (threshold) {
        var dist = ring.distanceTo(point);
        // only 8 digits of gate lat/lon are stored in db, causing uncertainty to be at least 1.2 units
        if(dist < 0.2) {
          return true;
        }
      }
      return false;
    }

    //address object format: obj.StreetAddress, obj.City, obj.State, obj.Zip
    function batchGeocodeAddresses(addresses, progressCallback) {
      var MAX_PER_BATCH = 100;
      var MIN_BATCHES = 6;

      var deferred = $q.defer();
      var results = [];
      var addressBatches = [];
      var counter = 0;

      //Split into MIN_BATCHES separate requests if over MIN_BATCHES addresses to geocode
      if (addresses.length < MIN_BATCHES) {
        addressBatches = [addresses];
      } else {
        //Increase the number of batches until the amount per batch is less than or equal to MAX_PER_BATCH.
        var num_batches = MIN_BATCHES - 1; //Start off at 1 less to account for first increment in do-while loop
        var num_in_batch;
        do {
          num_batches++;
          num_in_batch = Math.round(addresses.length / num_batches);
        }
        while (num_in_batch > MAX_PER_BATCH);
        //console.log("Splitting " + addresses.length + " entries into " + num_batches + " batches with " + num_in_batch + " entries each");

        //Split the addresses into "num_batches" batches
        addressBatches = chunkify(addresses, num_batches, true);
      }
      progressCallback(0, addressBatches.length);

      _.each(addressBatches, function (addressBatch) {
        counter++;
        var locations = [];
        _.each(addressBatch, function (address) {
          var location = {};
          //Address fields
          location.Address = address;

          //Location fields
          location.Region = PCMRegion[UserProvider.getUser().region];
          locations.push(location);
        });
        var request = {
          method: "POST",
          url: config.pcmwsUrl + "locations/address/batch",
          headers: {
            Authorization: config.alkmapsApiKey,
            BatchIndex: String(counter)
          },
          params: {
            dataset: UserProvider.getUser().dataset || UserProvider.getUser().dataVersion || "Current"
          },
          data: {
            Locations: locations
          }
        };
        return $http(request)
          .then(function (response) { //success
            results.push(response);
            progressCallback(results.length, addressBatches.length);
            if (results.length === addressBatches.length) {
              //The responses may not come back in the order they were sent so sort them first
              results.sort(function (a, b) {
                return Number(a.config.headers.BatchIndex) - Number(b.config.headers.BatchIndex);
              });
              //Because we split up calls to the batch geocoder, assemble responses here
              var mergedResults = [];
              for (let j = 0; j < results.length; j++) {
                mergedResults = mergedResults.concat(results[j].data);
              }
              deferred.resolve(mergedResults);
            }
          }, function (response) { //fail
            results = null;
            deferred.resolve(null);
          });
      });

      return deferred.promise;

    }

    //Split array a into n arrays (set balanced = true for equal subarray length)
    function chunkify(a, n, balanced) {
      if (n < 2) {
        return [a];
      }
      var len = a.length,
        out = [],
        i = 0,
        size;
      if (len % n === 0) {
        size = Math.floor(len / n);
        while (i < len) {
          out.push(a.slice(i, i += size));
        }
      } else if (balanced) {
        while (i < len) {
          size = Math.ceil((len - i) / n--);
          out.push(a.slice(i, i += size));
        }
      } else {
        n--;
        size = Math.floor(len / n);
        if (len % size === 0)
          size--;
        while (i < size * n) {
          out.push(a.slice(i, i += size));
        }
        out.push(a.slice(size * n));
      }
      return out;
    }

    return {
      //Constants
      invalidCharRegex: INVALID_CHAR_REGEX,

      //Helper functions
      browserImportIncompatible: browserImportIncompatible,
      filterErrors: filterErrors,
      testCompanyIds: testCompanyIds,
      importPlaces: importPlaces,
      importExceptions: importExceptions,
      isLatLonStringValid: isLatLonStringValid,
      getLatLonFromString: getLatLonFromString,
      isLatLonValid: isLatLonValid,
      isLatValid: isLatValid,
      isLonValid: isLonValid,
      getYardPointsFromString: getYardPointsFromString,
      getGatesFromString: getGatesFromString,
      getEncodedNote: getEncodedNote,
      getDecodedImportString: getDecodedImportString,
      strContainsLonLat: strContainsLonLat,
      containsLonLat: containsLonLat,
      batchGeocodeAddresses: batchGeocodeAddresses,

      //Import fields (since they are ES6 classes must be instantiated first)
      LatitudeField: new LatitudeField(),
      LongitudeField: new LongitudeField(),
      LatLonField: new LatLonField(),
      StreetAddressField: new StreetAddressField(),
      CityField: new CityField(),
      StateField: new StateField(),
      CountryAbbrevField: new CountryAbbrevField(),
      ZipField: new ZipField(),
      CountyField: new CountyField(),
      CountryField: new CountryField(),
      PlaceNameField: new PlaceNameField(),
      CompanyIdField: new CompanyIdField(),
      PhoneField: new PhoneField(),
      CommentsField: new CommentsField(),
      YardNameField: new YardNameField(),
      YardPointsField: new YardPointsField(),
      GatesField: new GatesField(),
      EntryLatLonField: new EntryLatLonField(),
      EntryLatLonSnapField: new EntryLatLonSnapField(),
      EntryNotesField: new EntryNotesField(),
      ExitLatLonField: new ExitLatLonField(),
      ExitLatLonSnapField: new ExitLatLonSnapField(),
      ExitNotesField: new ExitNotesField(),
      SiteDetailField: new SiteDetailField(),
      StopNameField: new StopNameField(),
      EstimatedDurationField: new EstimatedDurationField(),
      EarliestArrivalTimeField: new EarliestArrivalTimeField(),
      LatestArrivalTimeField: new LatestArrivalTimeField(),
    };

  }
})();



// WEBPACK FOOTER //
// ./Common Javascript Files/AngularJS/alk-import/alkImport.service.js